// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { ConfigProvider, Layout, Menu, message, notification } from 'antd';
import { TableOutlined, ExclamationCircleOutlined, WarningOutlined, MessageOutlined, InboxOutlined, SettingOutlined, ToolOutlined, SaveOutlined, OrderedListOutlined, FileOutlined, PartitionOutlined } from '@ant-design/icons';
import WorkflowBuilder from './components/WorkflowBuilder';
import WorkflowManagement from './components/WorkflowManagement';
import Settings from './components/settings';
import SavedOutputs from './components/SavedOutputs';
import WorkflowQueue from './components/WorkflowQueue';
import TemplatesPage from './components/TemplatesPage';
import ChatLogs from './components/ChatLogs';
import TasksPage from './components/TasksPage';
import LicenseOverlay from './components/LicenseOverlay';
import GamifiedPromoBanner from './components/GamifiedPromoBanner';
import './styles/CustomNodeStyles.css';
import './styles/chat.css';
import './styles/workflow-viewer.css';
import './styles/LockedNodeStyles.css';
import './styles/GamifiedPromoBanner.css';
import './styles/SetupWizard.css';
import axios from 'axios';
import config from './config';
import SetupWizard from './components/SetupWizard';

const { Header, Content } = Layout;

const getVersionBadge = () => {
  const isLite = process.env.REACT_APP_VERSION === 'lite';
  return {
    text: isLite ? 'LITE' : 'PRO',
    color: isLite ? '#52c41a' : '#2C5EFF', // Green for Lite, Blue for Pro
    width: isLite ? '58' : '54' // Slightly wider for "LITE"
  };
};

message.config({
  top: 40, 
  duration: 3, 
  maxCount: 2, 
});

const App = () => {
  const [currentPage, setCurrentPage] = useState('management');
  const [isLicenseActive, setIsLicenseActive] = useState(true);
  const [isLicenseTampered, setIsLicenseTampered] = useState(false);
  const [customLogo, setCustomLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [customHeaderText, setCustomHeaderText] = useState('AI Workflow Automation');
  const [customColors, setCustomColors] = useState({
    primaryColor: '#1890ff',
    menuBgColor: '#001529',
    menuTextColor: '#ffffff'
  });
  const isMounted = useRef(true);
  const [openRouterBalance, setOpenRouterBalance] = useState(null);
  const [hasShownLowBalanceWarning, setHasShownLowBalanceWarning] = useState(false);
  const [hasShownZeroBalanceWarning, setHasShownZeroBalanceWarning] = useState(false);

  useEffect(() => {
    // Check if it's Lite version
    const isLite = process.env.REACT_APP_VERSION === 'lite';
    
    if (isLite) {
      // For Lite version, always set license as active and not tampered
      setIsLicenseActive(true);
      setIsLicenseTampered(false);
    } else {
      // For Pro version, check the license
      checkLicenseStatus();
    }
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {

    const loadWhitelabelSettings = async () => {
      try {
    
        if (!window.wpAiWorkflowsWhitelabel) {
          return;
        }
    
        const settings = window.wpAiWorkflowsWhitelabel;
    
        // Check both possible property names for enabled status
        const isEnabled = settings.enabled === '1' || 
                        settings.enabled === true || 
                        settings.enable_whitelabel === '1' || 
                        settings.enable_whitelabel === true;
        
    
        if (isEnabled) {
          
          // Extract colors with proper fallbacks
          const primaryColor = settings.primaryColor || settings.primary_color || '#1890ff';
          const menuBgColor = settings.menuBgColor || settings.menu_bg_color || '#001529';
          const menuTextColor = settings.menuTextColor || settings.menu_text_color || '#ffffff';
          
          
          // Create new colors object - removed fontFamily
          const newColors = {
            primaryColor: primaryColor,
            menuBgColor: menuBgColor,
            menuTextColor: menuTextColor
          };
          
          // Set state for future reference
          setCustomColors(newColors);
          
          // Apply styles immediately with the colors we just extracted
          applyCustomStyles(newColors);
    
          // Handle logo loading
          const logoUrl = settings.logoUrl || settings.logo_url;
          if (logoUrl) {
            
            // Use a pre-load approach with Image API
            const logoImg = new Image();
            
            logoImg.onload = () => {
              
              // Force this into a separate execution context for React to recognize the state change
              setTimeout(() => {
                setCustomLogo(logoUrl);
                setLogoError(false);
                
                // Use the same colors object for consistency
                applyCustomStyles(newColors);
              }, 10);
            };
            
            logoImg.onerror = (error) => {
              console.error('Logo failed to load:', error);
              setLogoError(true);
              setCustomLogo(null);
            };
            
            // Start loading the image
            logoImg.src = logoUrl;
          } else {
            setCustomLogo(null);
          }
    
        } else {
          setCustomLogo(null);
          setCustomColors({
            primaryColor: '#1890ff',
            menuBgColor: '#001529',
            menuTextColor: '#ffffff'
          });
        }
      } catch (error) {
        
        // Set defaults on error
        setCustomLogo(null);
        setCustomColors({
          primaryColor: '#1890ff',
          menuBgColor: '#001529',
          menuTextColor: '#ffffff'
        });
      }
    };
    
    // And here's the modified applyCustomStyles function:
    
    const applyCustomStyles = (colorsToUse = null) => {
      // Use passed colors or fallback to state
      const colors = colorsToUse || customColors;
      
      try {
        // Remove any existing styles first
        const existingStyle = document.getElementById('wp-ai-workflows-whitelabel-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
    
        // Get colors with proper fallbacks - use the passed values or state values
        const primaryColor = colors.primaryColor || '#1890ff';
        const menuBgColor = colors.menuBgColor || '#001529';
        const menuTextColor = colors.menuTextColor || '#ffffff';
    
        const style = document.createElement('style');
        style.id = 'wp-ai-workflows-whitelabel-styles';
        
        const css = `
          /* Primary color for interactive elements */
          #wp-ai-workflows-root .ant-btn-primary { background-color: ${primaryColor} !important; border-color: ${primaryColor} !important; }
          #wp-ai-workflows-root .ant-btn-primary:hover { background-color: ${primaryColor} !important; filter: brightness(1.1); }
          #wp-ai-workflows-root a { color: ${primaryColor} !important; }
          #wp-ai-workflows-root .ant-menu-item-selected { color: ${primaryColor} !important; }
          #wp-ai-workflows-root .ant-switch-checked { background-color: ${primaryColor} !important; }
          
          /* Menu background color */
          #wp-ai-workflows-root .ant-layout-header { background-color: ${menuBgColor} !important; }
          #wp-ai-workflows-root .ant-menu.ant-menu-dark { background-color: ${menuBgColor} !important; }
          
          /* Menu text color */
          #wp-ai-workflows-root .ant-menu.ant-menu-dark .ant-menu-item, 
          #wp-ai-workflows-root .ant-menu.ant-menu-dark .ant-menu-item > a { color: ${menuTextColor} !important; }
          
          /* Logo specific styles to ensure visibility */
          #wp-ai-workflows-root .logo-container { 
            min-width: 180px !important;
            max-width: 520px !important;
            width: 30% !important;
            height: 32px !important;
            margin-top: 2px !important;
            margin-left: auto !important;
            margin-right: 50px !important; /* Add right margin */
            flex-shrink: 0 !important;
            position: relative !important;
            overflow: hidden !important;
            display: flex !important;
            justify-content: flex-end !important;
            align-items: center !important;
            text-align: right !important;
            z-index: 100 !important;
          }
          
          #wp-ai-workflows-root .logo-container img {
            width: auto !important;
            height: 100% !important;
            max-width: 100% !important;
            object-fit: contain !important;
            display: block !important;
          }
          
          /* SVG text and path colors */
          #wp-ai-workflows-root .logo-container svg text,
          #wp-ai-workflows-root .logo-container svg path {
            fill: ${menuTextColor} !important;
          }
        `;
        
        style.textContent = css;
        document.head.appendChild(style);
        
        return () => {
          if (style && style.parentNode) {
            style.parentNode.removeChild(style);
          }
        };
      } catch (error) {

      }
    };
  
    // Run the settings load
    loadWhitelabelSettings();
  
    // Cleanup function
    return () => {
      const existingStyle = document.getElementById('wp-ai-workflows-whitelabel-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const checkLicenseStatus = async () => {
    // Double-check if it's Lite version (defensive programming)
    const isLite = process.env.REACT_APP_VERSION === 'lite';
    
    if (isLite) {
      // For Lite version, skip license check entirely
      setIsLicenseActive(true);
      setIsLicenseTampered(false);
      return;
    }

    try {
      const response = await axios.get(`${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/check-license`, {
        headers: {
          'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
        }
      });
  
      setIsLicenseActive(response.data.is_active);
  
      if (response.data.is_active && !response.data.token) {
        setIsLicenseTampered(true);
        message.error('License verification failed. Please make sure you are using a valid license key.');
      } else {
        setIsLicenseTampered(false);
      }
    } catch (error) {
      console.error('Error checking license status:', error);
      setIsLicenseActive(false);
      setIsLicenseTampered(true);
    }
  };


  const logoStyle = {
    minWidth: '180px',
    maxWidth: '520px',
    width: '30%',
    height: '32px',
    marginTop: '2px',
    marginLeft: 'auto', 
    marginRight: '50', 
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'right', 
    display: 'flex',     
    justifyContent: 'flex-end' 
  };
  
  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '0 16px',
    background: customColors.menuBgColor,
    position: 'relative'
  };
  
  const menuStyle = {
    flex: 1,
    minWidth: 0,
    marginRight: '16px',
    background: 'transparent' 
  };

  // Add this function to check OpenRouter balance
  const checkOpenRouterBalance = async () => {
    try {
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/check-openrouter-balance`,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );

      if (response.data.success) {
        const balance = response.data.balance;
        setOpenRouterBalance(balance);

        // Check for low balance (under $5)
        if (balance < 5 && balance > 0 && !hasShownLowBalanceWarning) {
          setHasShownLowBalanceWarning(true);
          notification.warning({
            message: 'Low OpenRouter Balance',
            description: (
              <div>
                Your OpenRouter balance is low (${balance.toFixed(2)}). This may cause workflows, 
                workflow generator, and workflow assistant to stop working soon. Recharge your account to avoid.
                <br /><br />
                <a 
                  href="https://openrouter.ai/settings/credits" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ fontWeight: 'bold' }}
                >
                  Add credits to your wallet →
                </a>
              </div>
            ),
            icon: <WarningOutlined style={{ color: '#faad14' }} />,
            duration: 0, // Don't auto-close
            placement: 'topRight',
            style: {
              width: 400
            }
          });
        }

        // Check for zero/critical balance
        if (balance <= 0 && !hasShownZeroBalanceWarning) {
          setHasShownZeroBalanceWarning(true);
          notification.error({
            message: 'Critical: No OpenRouter Balance',
            description: (
              <div>
                Your OpenRouter balance is empty. Workflows, workflow generator, 
                and workflow assistant will not work until you add credits.
                <br /><br />
                <a 
                  href="https://openrouter.ai/settings/credits" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ fontWeight: 'bold', color: '#ff4d4f' }}
                >
                  Add credits now →
                </a>
              </div>
            ),
            icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
            duration: 0, // Don't auto-close
            placement: 'topRight',
            style: {
              width: 400
            }
          });
        }

        // Reset warning flags if balance is restored
        if (balance >= 5) {
          setHasShownLowBalanceWarning(false);
          setHasShownZeroBalanceWarning(false);
        }
      }
    } catch (error) {
      //console.error('Error checking OpenRouter balance:', error);
    }
  };

  // Add this useEffect to check balance periodically
  useEffect(() => {
    // Check balance on mount
    checkOpenRouterBalance();

    // Check balance every 5 minutes
    const interval = setInterval(checkOpenRouterBalance, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [hasShownLowBalanceWarning, hasShownZeroBalanceWarning]);

  // Also check balance when page changes (to catch it sooner)
  useEffect(() => {
    checkOpenRouterBalance();
  }, [currentPage]);

  const getCurrentPage = () => {
    if (window.wpAiWorkflowsSettings && window.wpAiWorkflowsSettings.page) {
      return window.wpAiWorkflowsSettings.page;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');
    
    if (page === 'wp-ai-workflows-tasks') {
      return 'tasks';
    }
    
    return urlParams.get('action') || 'management';
  };

  const renderContent = () => {
    const currentPage = getCurrentPage();
  
    switch (currentPage) {
      case 'tasks':
        return <TasksPage />;
      case 'builder':
      case 'edit':
        return <WorkflowBuilder />;
      case 'management':
        return <WorkflowManagement />;
      case 'settings':
        // Pass a modified onLicenseUpdate that respects Lite version
        return <Settings onLicenseUpdate={() => {
          const isLite = process.env.REACT_APP_VERSION === 'lite';
          if (!isLite) {
            checkLicenseStatus();
          }
        }} />;
      case 'saved-outputs':
        return <SavedOutputs />;
      case 'queue':
        return <WorkflowQueue />;
      case 'templates':
        return <TemplatesPage />;
      case 'chat-logs':
        return <ChatLogs />;
      default:
        if (currentPage === 'wp-ai-workflows') {
          return <WorkflowBuilder />;
        }
        return <div>Page not found. Current page: {currentPage}</div>;
    }
  };

  const onMenuClick = (item) => {
    const baseUrl = window.location.href.split('?')[0];
    window.location.href = `${baseUrl}?page=wp-ai-workflows&action=${item.key}`;
  };

  const handleSettingsClick = () => {
    const baseUrl = window.location.href.split('?')[0];
    window.location.href = `${baseUrl}?page=wp-ai-workflows&action=settings`;
  };

  // Check if Lite version for determining whether to show LicenseOverlay and upgrade button
  const isLiteVersion = process.env.REACT_APP_VERSION === 'lite';

  return (
    <ConfigProvider>
      <Layout className="layout" id="wp-ai-workflows-root" style={{ minHeight: '100vh' }}>
      <SetupWizard isLicenseActive={isLicenseActive} />
      <Header style={headerStyle}>
      <Menu 
        theme="dark" 
        mode="horizontal" 
        defaultSelectedKeys={[getCurrentPage()]} 
        onClick={onMenuClick}
        style={menuStyle}
      >
          <Menu.Item key="builder" icon={<PartitionOutlined />}>
            Workflow Builder
          </Menu.Item>
          <Menu.Item key="management" icon={<SettingOutlined />}>
            Management
          </Menu.Item>
          <Menu.Item key="templates" icon={<FileOutlined />}>
            Templates
          </Menu.Item>
          <Menu.Item key="saved-outputs" icon={<TableOutlined />}>
            Tables
          </Menu.Item>
          <Menu.Item key="queue" icon={<OrderedListOutlined />}>
            Queue
          </Menu.Item>
          <Menu.Item key="chat-logs" icon={<MessageOutlined />}>
            Chats
          </Menu.Item>
          <Menu.Item key="settings" icon={<ToolOutlined />}>
            Settings
          </Menu.Item>
      </Menu>

  {/* Upgrade to Pro button for Lite users */}
  {isLiteVersion && (
    <div style={{ marginRight: '20px' }}>
      <a 
        href="https://wpaiworkflowautomation.com/#pricing?utm_source=wp-ai-workflows-lite&utm_medium=plugin&utm_campaign=upgrade-to-pro&utm_content=header-button" 
        target="_blank" 
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#ffffff',
          borderRadius: '4px',
          textDecoration: 'none',
          fontWeight: '500',
          fontSize: '13px',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          //border: '1px solid rgba(255,255,255,0.2)',
          height: '24px',
          lineHeight: '24px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }}
      >
        <span style={{ marginRight: '6px' }}>🚀</span>
        Upgrade to PRO
      </a>
    </div>
  )}

  <div className="logo-container" style={{ 
    display: 'flex', 
    alignItems: 'center',
    minWidth: '180px',
    maxWidth: '520px',
    width: '30%',
    height: '32px',
    marginTop: '2px',
    marginLeft: 'auto',
    marginRight: '50px'
  }}>
    {customLogo && !logoError ? (
      <img
        src={customLogo}
        alt="Logo"
        style={{
          height: '100%',
          width: 'auto',
          maxHeight: '32px',
          objectFit: 'contain',
          marginLeft: 'auto'
        }}
        onError={() => setLogoError(true)}
      />
    ) : (
      <svg width="620" height="50" viewBox="0 0 620 50" style={{ marginLeft: 'auto' }}>
        <g transform="translate(10, -6) scale(0.15)">
          <path fill={customColors.menuTextColor || "#ffffff"} d="M247.96,269.25h-.06s0,.05,0,.08c0-.03,0-.06,0-.08-32.84-.02-61,.03-66.53,.27-39.91,1.73-60.1-28.83-60.1-59.14,0-.09-31.12-.09-31.12,0-1.29,61.87-44.36,60.13-88.33,59.16-.17,13.74,0,29.77,0,29.77H31.35c30.77,0,58.97,24.06,59.64,59.44-.02,0-.02,30.88,0,30.88h31.15c2.43-27.34-14.19-86.53,58.46-90.32,25.55,0,47.92,0,67.44,0,0-.04,.03-25.78-.06-30.07Zm.06,30.07h30.47c-9.28,0-19.42,0-30.47,0h0ZM1.6,210.32H90.04s0-24.13,0-29.72c-45.06-.02-88.45-.05-88.45-.05v29.77Zm246.36,58.93c9.93,0,20.28,.02,30.56,.04,.79-35.25,29.15-58.97,59.86-58.97h29.54s.17-16.03,0-29.77c-9.81,0-26.1-.05-30.16-.07-30.84,0-58.66-15.27-58.66-60.45,0-.01,0-.02,0-.03h-31.27c0,.59,0,1.19,0,1.81,.71,34.3-19.64,58.71-59.45,58.71-5.57,.07-34.06,.09-67.18,.08-.1,12.83,0,29.88,0,29.71h67.95c56.55,0,58.57,46.67,58.75,58.93h0c.03-.84,.05-.76,.06,0Zm30.56,.04c0,.08,0,.16-.01,.23-.15,8.7-.02,29.78-.02,29.79,67.26,0,89.64,0,89.64,0v-29.77s-44.14-.17-89.61-.25ZM179.65,120h68.17c0-20.56,.09-18.94,.09-29.68h30.68s.33,12.73,.5,29.68h88.1v-29.77s-172.15,.15-186.77-.02c-39.82,0-60.17-24.41-59.45-58.71C120.97,9.01,120.87,11.05,120.87,0h-30.68s-.54,29.71-.5,29.71c0,45.18-27.82,60.45-58.66,60.45-4.05,.01-20.35,.07-30.16,.07-.17,13.74,0,29.77,0,29.77H30.42c30.77,0,58.97,24.06,59.64,59.44,0,0,0,.43,0,1.16,10.46,0,21.02,0,31.14,0,0-.44,0-.87,.01-1.29,.17-12.13,1.78-59.31,58.45-59.31Z" />
        </g>
        
        <text x="100" y="35" fontFamily="IBM Plex Mono, monospace" fontWeight="600" fontSize="32" fill={customColors.menuTextColor || "#ffffff"}>
          AI Workflow Automation
        </text>
        
        {(() => {
          const badge = getVersionBadge();
          return (
            <>
              <rect x="550" y="10" width={badge.width} height="30" rx="15" fill={badge.color} filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
              <text x={badge.text === 'LITE' ? '560' : '562'} y="32" fontFamily="IBM Plex Mono, monospace" fontWeight="600" fontSize="18" fill="#ffffff">
                {badge.text}
              </text>
            </>
          );
        })()}
      </svg>
    )}
  </div>
      </Header>
        
        <Content
          style={
            getCurrentPage() === 'builder' || getCurrentPage() === 'edit'
              ? { padding: '0' }
              : { padding: '0 50px' }
          }
        >
          <div
            className="site-layout-content"
            style={
              getCurrentPage() === 'builder' || getCurrentPage() === 'edit'
                ? { margin: '0' }
                : { margin: '16px 0' }
            }
          >
            {renderContent()}
          </div>
        </Content>

        {/* Only show LicenseOverlay for Pro version */}
        {!isLiteVersion && (
          <LicenseOverlay
            visible={(!isLicenseActive || isLicenseTampered) && getCurrentPage() !== 'settings'}
            onSettingsClick={handleSettingsClick}
            isTampered={isLicenseTampered}
          />
        )}
         <GamifiedPromoBanner />
      </Layout>
    </ConfigProvider>
  );
};

window.wpAiWorkflowsApplySettings = function(settings) {
  
  // Update colors
  const colors = {
    primaryColor: settings.primaryColor || '#1890ff',
    menuBgColor: settings.menuBgColor || '#001529',
    menuTextColor: settings.menuTextColor || '#ffffff'
  };
  
  // Apply styles
  const existingStyle = document.getElementById('wp-ai-workflows-whitelabel-styles');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  const style = document.createElement('style');
  style.id = 'wp-ai-workflows-whitelabel-styles';
  
  const css = `
    /* Primary color for interactive elements */
    #wp-ai-workflows-root .ant-btn-primary { background-color: ${colors.primaryColor} !important; border-color: ${colors.primaryColor} !important; }
    #wp-ai-workflows-root .ant-btn-primary:hover { background-color: ${colors.primaryColor} !important; filter: brightness(1.1); }
    #wp-ai-workflows-root a { color: ${colors.primaryColor} !important; }
    #wp-ai-workflows-root .ant-menu-item-selected { color: ${colors.primaryColor} !important; }
    #wp-ai-workflows-root .ant-switch-checked { background-color: ${colors.primaryColor} !important; }
    
    /* Menu background color */
    #wp-ai-workflows-root .ant-layout-header { background-color: ${colors.menuBgColor} !important; }
    #wp-ai-workflows-root .ant-menu.ant-menu-dark { background-color: ${colors.menuBgColor} !important; }
    
    /* Menu text color */
    #wp-ai-workflows-root .ant-menu.ant-menu-dark .ant-menu-item, 
    #wp-ai-workflows-root .ant-menu.ant-menu-dark .ant-menu-item > a { color: ${colors.menuTextColor} !important; }
    
    /* Logo styles */
      #wp-ai-workflows-root .logo-container { 
        min-width: 180px !important;
        max-width: 520px !important;
        width: 30% !important;
        height: 32px !important;
        margin-top: 2px !important;
        margin-left: auto !important;
        margin-right: 50px !important; /* Add right margin */
        flex-shrink: 0 !important;
        position: relative !important;
        overflow: hidden !important;
        display: flex !important;
        justify-content: flex-end !important;
        align-items: center !important;
        text-align: right !important;
        z-index: 100 !important;
      }
  `;
  
  style.textContent = css;
  document.head.appendChild(style);
  
  // Update logo if provided
  if (settings.logoUrl) {
    const logoContainer = document.querySelector('#wp-ai-workflows-root .logo-container');
    if (logoContainer) {
      logoContainer.innerHTML = ''; 
      logoContainer.style.display = 'flex';
      logoContainer.style.justifyContent = 'flex-end';
      logoContainer.style.marginRight = '50px'; 
      
      // Add new image
      const img = document.createElement('img');
      img.src = settings.logoUrl;
      img.alt = 'Logo';
      img.style.height = '100%';
      img.style.width = 'auto';
      img.style.maxHeight = '32px';
      img.style.objectFit = 'contain';
      img.style.marginLeft = 'auto';
      
      logoContainer.appendChild(img);
    }
  }
};

export default App;